import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Skeleton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Search,
  Add,
  PlaylistPlay,
  Delete,
  Publish,
  Unpublished,
  Refresh,
  ArrowBack,
  CheckCircle,
  Cancel,
  AccessTime,
  ClosedCaption,
  Quiz,
  Warning,
  YouTube,
  AutoFixHigh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { youtubeService, PlaylistPreview, ParsedVideo } from '../services/youtubeService';
import { YouTubePlaylist, VideoContent } from '../types';

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES = [
  'Science',
  'Podcast',
  'Skits',
  'Animals',
  'Love',
  'Health',
  'News',
  'History',
];

type FilterStatus = 'all' | 'published' | 'draft' | 'processing';

const ADD_STEPS = ['Paste URL', 'Preview Videos', 'Select Category', 'Confirm'];

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatusChip: React.FC<{ playlist: YouTubePlaylist }> = ({ playlist }) => {
  if (playlist.isPublished) {
    return <Chip label="Published" color="success" size="small" icon={<Publish />} />;
  }
  if (playlist.videosProcessed < playlist.videoCount) {
    return <Chip label="Processing" color="warning" size="small" icon={<AccessTime />} />;
  }
  return <Chip label="Draft" color="default" size="small" variant="outlined" />;
};

const getPlaylistStatus = (p: YouTubePlaylist): FilterStatus => {
  if (p.isPublished) return 'published';
  if (p.videosProcessed < p.videoCount) return 'processing';
  return 'draft';
};

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const YouTubePlaylistPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // List view state
  const [playlists, setPlaylists] = useState<YouTubePlaylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  // Detail view state
  const [selectedPlaylist, setSelectedPlaylist] = useState<YouTubePlaylist | null>(null);
  const [playlistVideos, setPlaylistVideos] = useState<VideoContent[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [generatingVideoIds, setGeneratingVideoIds] = useState<Set<string>>(new Set());
  const [batchGenerating, setBatchGenerating] = useState(false);
  const batchCancelledRef = useRef(false);

  // Add playlist dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [preview, setPreview] = useState<PlaylistPreview | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [inserting, setInserting] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: 'playlist' | 'video';
    id: string;
    title: string;
  }>({ open: false, type: 'playlist', id: '', title: '' });

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchPlaylists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await youtubeService.getPlaylists();
      if (response.success) {
        setPlaylists(response.data);
      } else {
        enqueueSnackbar(response.error || 'Failed to fetch playlists', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to fetch playlists', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchPlaylistVideos = useCallback(async (playlistId: string) => {
    try {
      setVideosLoading(true);
      const response = await youtubeService.getPlaylistVideos(playlistId);
      if (response.success) {
        setPlaylistVideos(response.data);
      } else {
        enqueueSnackbar(response.error || 'Failed to fetch videos', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to fetch videos', { variant: 'error' });
    } finally {
      setVideosLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  useEffect(() => {
    if (selectedPlaylist) {
      fetchPlaylistVideos(selectedPlaylist.id);
    }
  }, [selectedPlaylist, fetchPlaylistVideos]);

  // ============================================================================
  // FILTERED PLAYLISTS
  // ============================================================================

  const filteredPlaylists = playlists.filter(p => {
    const matchesSearch = !searchQuery
      || p.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || getPlaylistStatus(p) === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // ============================================================================
  // ADD PLAYLIST FLOW
  // ============================================================================

  const handleFetchPreview = async () => {
    if (!playlistUrl.trim()) return;
    setFetchingPreview(true);
    const response = await youtubeService.fetchPlaylistPreview(playlistUrl);
    setFetchingPreview(false);

    if (response.success) {
      setPreview(response.data);
      setActiveStep(1);
    } else {
      enqueueSnackbar(response.error || 'Failed to fetch playlist', { variant: 'error' });
    }
  };

  const handleInsertPlaylist = async () => {
    if (!preview || !selectedCategory) return;
    setInserting(true);
    const response = await youtubeService.insertPlaylist(preview, selectedCategory);
    setInserting(false);

    if (response.success) {
      enqueueSnackbar('Playlist added successfully!', { variant: 'success' });
      handleCloseAddDialog();
      fetchPlaylists();
    } else {
      enqueueSnackbar(response.error || 'Failed to insert playlist', { variant: 'error' });
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setActiveStep(0);
    setPlaylistUrl('');
    setPreview(null);
    setSelectedCategory('');
    setFetchingPreview(false);
    setInserting(false);
  };

  // ============================================================================
  // CONTENT GENERATION
  // ============================================================================

  const handleGenerateQuiz = async (video: VideoContent) => {
    if (!video.youtubeVideoId) return;
    setGeneratingVideoIds(prev => new Set(prev).add(video.id));

    const response = await youtubeService.generateQuizAndVocabulary(
      video.id,
      video.youtubeVideoId,
      video.title,
      video.subject || 'General',
      video.youtubeChannelHandle || undefined,
      video.youtubeChannelIconUrl || undefined,
    );

    setGeneratingVideoIds(prev => {
      const next = new Set(prev);
      next.delete(video.id);
      return next;
    });

    if (response.success) {
      enqueueSnackbar(`Quiz generated for "${video.title}"`, { variant: 'success' });
      if (selectedPlaylist) {
        fetchPlaylistVideos(selectedPlaylist.id);
        fetchPlaylists();
      }
    } else {
      enqueueSnackbar(response.error || 'Failed to generate quiz', { variant: 'error' });
    }
  };

  const handleRegenerateQuiz = async (video: VideoContent) => {
    if (!video.youtubeVideoId) return;
    setGeneratingVideoIds(prev => new Set(prev).add(video.id));

    const response = await youtubeService.regenerateQuiz(
      video.id,
      video.youtubeVideoId,
      video.title,
      video.subject || 'General',
    );

    setGeneratingVideoIds(prev => {
      const next = new Set(prev);
      next.delete(video.id);
      return next;
    });

    if (response.success) {
      enqueueSnackbar(`Quiz regenerated for "${video.title}"`, { variant: 'success' });
      if (selectedPlaylist) {
        fetchPlaylistVideos(selectedPlaylist.id);
        fetchPlaylists();
      }
    } else {
      enqueueSnackbar(response.error || 'Failed to regenerate quiz', { variant: 'error' });
    }
  };

  const handleBatchGenerate = async () => {
    const unprocessed = playlistVideos.filter(
      v => !v.quizGenerated && v.youtubeVideoId
    );
    if (unprocessed.length === 0) return;

    setBatchGenerating(true);
    batchCancelledRef.current = false;
    const concurrencyLimit = 2;

    const queue = [...unprocessed];
    const runNext = async (): Promise<void> => {
      while (queue.length > 0 && !batchCancelledRef.current) {
        const video = queue.shift()!;
        setGeneratingVideoIds(prev => new Set(prev).add(video.id));

        const response = await youtubeService.generateQuizAndVocabulary(
          video.id,
          video.youtubeVideoId!,
          video.title,
          video.subject || 'General',
          video.youtubeChannelHandle || undefined,
          video.youtubeChannelIconUrl || undefined,
        );

        setGeneratingVideoIds(prev => {
          const next = new Set(prev);
          next.delete(video.id);
          return next;
        });

        if (!response.success) {
          enqueueSnackbar(`Failed: ${video.title}`, { variant: 'error' });
        }

        if (selectedPlaylist) {
          fetchPlaylistVideos(selectedPlaylist.id);
        }
      }
    };

    const workers = Array.from({ length: Math.min(concurrencyLimit, queue.length) }, () => runNext());
    await Promise.all(workers);

    setBatchGenerating(false);
    fetchPlaylists();
    enqueueSnackbar('Batch generation complete', { variant: 'success' });
  };

  // ============================================================================
  // PUBLISH / UNPUBLISH / DELETE
  // ============================================================================

  const handlePublish = async () => {
    if (!selectedPlaylist) return;
    const response = await youtubeService.publishPlaylist(selectedPlaylist.id);
    if (response.success) {
      enqueueSnackbar('Playlist published', { variant: 'success' });
      setSelectedPlaylist({ ...selectedPlaylist, isPublished: true });
      fetchPlaylists();
    } else {
      enqueueSnackbar(response.error || 'Failed to publish', { variant: 'error' });
    }
  };

  const handleUnpublish = async () => {
    if (!selectedPlaylist) return;
    const response = await youtubeService.unpublishPlaylist(selectedPlaylist.id);
    if (response.success) {
      enqueueSnackbar('Playlist unpublished', { variant: 'success' });
      setSelectedPlaylist({ ...selectedPlaylist, isPublished: false });
      fetchPlaylists();
    } else {
      enqueueSnackbar(response.error || 'Failed to unpublish', { variant: 'error' });
    }
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteDialog;
    if (type === 'playlist') {
      const response = await youtubeService.deletePlaylist(id);
      if (response.success) {
        enqueueSnackbar('Playlist deleted', { variant: 'success' });
        setSelectedPlaylist(null);
        fetchPlaylists();
      } else {
        enqueueSnackbar(response.error || 'Failed to delete playlist', { variant: 'error' });
      }
    } else {
      const response = await youtubeService.deleteVideo(id);
      if (response.success) {
        enqueueSnackbar('Video deleted', { variant: 'success' });
        if (selectedPlaylist) {
          fetchPlaylistVideos(selectedPlaylist.id);
          fetchPlaylists();
        }
      } else {
        enqueueSnackbar(response.error || 'Failed to delete video', { variant: 'error' });
      }
    }
    setDeleteDialog({ open: false, type: 'playlist', id: '', title: '' });
  };

  // ============================================================================
  // RENDER: PLAYLIST DETAIL VIEW
  // ============================================================================

  if (selectedPlaylist) {
    const quizCount = playlistVideos.filter(v => v.quizGenerated).length;
    const vocabCount = playlistVideos.filter(v => v.vocabularyGenerated).length;
    const totalVideos = playlistVideos.length;
    const allProcessed = totalVideos > 0 && quizCount === totalVideos && vocabCount === totalVideos;
    const unprocessedCount = playlistVideos.filter(v => !v.quizGenerated && v.youtubeVideoId).length;
    const progress = totalVideos > 0 ? (quizCount / totalVideos) * 100 : 0;

    return (
      <Box sx={{ p: 3 }}>
        {/* Back Button & Header */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => setSelectedPlaylist(null)}
          sx={{ mb: 2 }}
        >
          Back to Playlists
        </Button>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={3} flexWrap="wrap">
              {selectedPlaylist.thumbnailUrl && (
                <Box
                  component="img"
                  src={selectedPlaylist.thumbnailUrl}
                  alt={selectedPlaylist.title}
                  sx={{ width: 200, height: 112, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                />
              )}
              <Box flex={1} minWidth={250}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h5" fontWeight="bold">{selectedPlaylist.title}</Typography>
                  <StatusChip playlist={selectedPlaylist} />
                </Box>
                {selectedPlaylist.description && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {selectedPlaylist.description}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Added {format(new Date(selectedPlaylist.createdAt), 'PPP')}
                </Typography>

                {/* Progress */}
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight="medium">
                      Quizzes: {quizCount}/{totalVideos} &nbsp;&bull;&nbsp; Vocabulary: {vocabCount}/{totalVideos}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(progress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {/* Action Buttons */}
                <Box display="flex" gap={1} flexWrap="wrap">
                  {unprocessedCount > 0 && (
                    <Button
                      variant="contained"
                      startIcon={batchGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoFixHigh />}
                      onClick={handleBatchGenerate}
                      disabled={batchGenerating}
                    >
                      {batchGenerating ? 'Generating...' : `Generate All (${unprocessedCount})`}
                    </Button>
                  )}
                  {!selectedPlaylist.isPublished && allProcessed && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Publish />}
                      onClick={handlePublish}
                    >
                      Publish
                    </Button>
                  )}
                  {selectedPlaylist.isPublished && (
                    <Button
                      variant="outlined"
                      startIcon={<Unpublished />}
                      onClick={handleUnpublish}
                    >
                      Unpublish
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteDialog({
                      open: true,
                      type: 'playlist',
                      id: selectedPlaylist.id,
                      title: selectedPlaylist.title,
                    })}
                  >
                    Delete Playlist
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Video Table */}
        {videosLoading ? (
          <Box>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : playlistVideos.length === 0 ? (
          <Alert severity="info">No videos found in this playlist.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Video</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Quiz</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Vocabulary</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {playlistVideos.map(video => {
                  const isGenerating = generatingVideoIds.has(video.id);
                  return (
                    <TableRow key={video.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            component="img"
                            src={video.thumbnailUrl}
                            alt={video.title}
                            sx={{ width: 80, height: 45, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
                          />
                          <Box minWidth={0}>
                            <Typography variant="body2" fontWeight="medium" noWrap>
                              {video.title}
                            </Typography>
                            {video.youtubeVideoId && (
                              <Typography variant="caption" color="text.secondary">
                                {video.youtubeVideoId}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatDuration(video.durationSeconds)}
                          size="small"
                          icon={<AccessTime />}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {video.quizGenerated ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="disabled" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {video.vocabularyGenerated ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="disabled" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                          {!video.quizGenerated ? (
                            <Tooltip title="Generate Quiz & Vocabulary">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleGenerateQuiz(video)}
                                  disabled={isGenerating}
                                >
                                  {isGenerating ? <CircularProgress size={18} /> : <AutoFixHigh />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Regenerate Quiz & Vocabulary">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleRegenerateQuiz(video)}
                                  disabled={isGenerating}
                                >
                                  {isGenerating ? <CircularProgress size={18} /> : <Refresh />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Video">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({
                                open: true,
                                type: 'video',
                                id: video.id,
                                title: video.title,
                              })}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, type: 'playlist', id: '', title: '' })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Delete {deleteDialog.type === 'playlist' ? 'Playlist' : 'Video'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{deleteDialog.title}</strong>?
              {deleteDialog.type === 'playlist' && ' All associated videos, quizzes, and vocabulary will also be deleted.'}
              {' '}This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, type: 'playlist', id: '', title: '' })}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: PLAYLIST LIST VIEW
  // ============================================================================

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <YouTube color="error" fontSize="large" />
          <Typography variant="h4" component="h1" fontWeight="bold">
            YouTube Playlists
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Playlist
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search playlists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPlaylists}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Playlist Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <Skeleton variant="rectangular" height={160} />
                <CardContent>
                  <Skeleton width="70%" />
                  <Skeleton width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredPlaylists.length === 0 ? (
        <Box textAlign="center" py={8}>
          <PlaylistPlay sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {playlists.length === 0 ? 'No playlists yet' : 'No matching playlists'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {playlists.length === 0
              ? 'Add a YouTube playlist to get started.'
              : 'Try adjusting your search or filter.'}
          </Typography>
          {playlists.length === 0 && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
              Add Playlist
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredPlaylists.map(playlist => {
            const progress = playlist.videoCount > 0
              ? (playlist.videosProcessed / playlist.videoCount) * 100
              : 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={playlist.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 6 },
                  }}
                >
                  <CardActionArea onClick={() => setSelectedPlaylist(playlist)}>
                    <CardMedia
                      component="img"
                      height={160}
                      image={playlist.thumbnailUrl || '/placeholder-playlist.png'}
                      alt={playlist.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ flex: 1, mr: 1 }}>
                          {playlist.title}
                        </Typography>
                        <StatusChip playlist={playlist} />
                      </Box>
                      <Box display="flex" gap={1} mb={1.5}>
                        <Chip
                          label={`${playlist.videoCount} videos`}
                          size="small"
                          variant="outlined"
                          icon={<PlaylistPlay />}
                        />
                        <Chip
                          label={`${playlist.videosProcessed}/${playlist.videoCount} processed`}
                          size="small"
                          variant="outlined"
                          icon={<Quiz />}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 4, borderRadius: 2, mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Added {format(new Date(playlist.createdAt), 'MMM dd, yyyy')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ================================================================== */}
      {/* ADD PLAYLIST DIALOG                                                */}
      {/* ================================================================== */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: 480 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <YouTube color="error" />
            <Typography variant="h6">Add YouTube Playlist</Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {ADD_STEPS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Paste URL */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Paste a YouTube playlist URL below. We'll fetch the playlist and filter for short videos (3 minutes or less).
              </Typography>
              <TextField
                fullWidth
                label="YouTube Playlist URL"
                placeholder="https://www.youtube.com/playlist?list=PLxxxxxx"
                value={playlistUrl}
                onChange={(e) => setPlaylistUrl(e.target.value)}
                disabled={fetchingPreview}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <YouTube color="error" />
                    </InputAdornment>
                  ),
                }}
              />
              {fetchingPreview && (
                <Box mt={2}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
                    Fetching playlist from YouTube...
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Step 1: Preview Videos */}
          {activeStep === 1 && preview && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Found <strong>{preview.totalCount}</strong> videos.{' '}
                <strong>{preview.eligibleCount}</strong> eligible (&#8804; 3 min).{' '}
                {preview.totalCount - preview.eligibleCount > 0 && (
                  <span>{preview.totalCount - preview.eligibleCount} will be skipped.</span>
                )}
              </Alert>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Duration</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Captions</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.videos.map((video: ParsedVideo) => (
                      <TableRow
                        key={video.youtubeVideoId}
                        sx={{
                          opacity: video.eligible ? 1 : 0.5,
                          bgcolor: video.eligible ? 'inherit' : 'action.disabledBackground',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
                            {video.title}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={video.durationFormatted}
                            size="small"
                            color={video.eligible ? 'default' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {video.hasCaptions ? (
                            <ClosedCaption color="success" fontSize="small" />
                          ) : (
                            <ClosedCaption color="disabled" fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {video.eligible ? (
                            <CheckCircle color="success" fontSize="small" />
                          ) : (
                            <Tooltip title={video.ineligibleReason || 'Ineligible'}>
                              <Warning color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Step 2: Select Category */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Choose a category for all videos in this playlist.
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Step 3: Confirm */}
          {activeStep === 3 && preview && (
            <Box>
              <Typography variant="h6" gutterBottom>Review</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Playlist</Typography>
                  <Typography variant="body1" fontWeight="medium">{preview.title}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Eligible Videos</Typography>
                  <Typography variant="body1" fontWeight="medium">{preview.eligibleCount}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Typography variant="body1" fontWeight="medium">{selectedCategory}</Typography>
                </Grid>
              </Grid>
              {inserting && (
                <Box mt={3}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
                    Inserting playlist and videos...
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Box flex={1} />
          {activeStep > 0 && activeStep < 3 && (
            <Button onClick={() => setActiveStep(prev => prev - 1)}>
              Back
            </Button>
          )}
          {activeStep === 0 && (
            <Button
              variant="contained"
              onClick={handleFetchPreview}
              disabled={!playlistUrl.trim() || fetchingPreview}
            >
              Fetch Playlist
            </Button>
          )}
          {activeStep === 1 && preview && preview.eligibleCount > 0 && (
            <Button variant="contained" onClick={() => setActiveStep(2)}>
              Next
            </Button>
          )}
          {activeStep === 2 && (
            <Button
              variant="contained"
              onClick={() => setActiveStep(3)}
              disabled={!selectedCategory}
            >
              Next
            </Button>
          )}
          {activeStep === 3 && (
            <Button
              variant="contained"
              color="success"
              onClick={handleInsertPlaylist}
              disabled={inserting}
              startIcon={inserting ? <CircularProgress size={18} color="inherit" /> : <Add />}
            >
              {inserting ? 'Adding...' : 'Add Playlist'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YouTubePlaylistPage;
